import type {
  ActionConfig,
  ActionDescription,
  Member,
  Team,
} from "@smartsuite/automation-engine-api";
import type { MembersStore } from "../stores/member.store";
import type { TeamsStore } from "../stores/teams";
import { actionSolutionId } from "./action-solutions";

/** The members and teams that may be selected on one particular input. */
export interface MemberScope {
  members: Member[];
  teams: Team[];
}

interface DirectoryStores {
  members: MembersStore;
  teams: TeamsStore;
}

interface ScopedAction extends ActionConfig {
  description?: ActionDescription;
}

/**
 * Notification recipients are addressed directly and don't read the record, so
 * any account member is a legitimate target regardless of solution permissions.
 */
export const workspaceMemberScope = (stores: DirectoryStores): MemberScope => ({
  members: stores.members.workspace,
  teams: stores.teams.workspace,
});

/**
 * Members and teams with access to `solutionId`. A record's user field may only
 * hold members who can actually see that record.
 */
export const solutionMemberScope = (
  stores: DirectoryStores,
  solutionId: string,
): MemberScope => ({
  members: stores.members.forSolution(solutionId),
  teams: stores.teams.forSolution(solutionId),
});

/** True when the input is edited with the email / notification recipient editor. */
const isRecipientInput = (
  inputId: string,
  description: ActionDescription | undefined,
): boolean =>
  description?.inputs.find((input) => input.inputId === inputId)?.editor?.editor
    .oneofKind === "email";

/**
 * The scope an input's saved members and teams must resolve against -- the same
 * set its picker offers, so validation can never reject a value the picker
 * allowed, nor accept one it hid.
 */
export const memberScopeForInput = (
  stores: DirectoryStores,
  hostSolutionId: string,
  inputId: string,
  action?: ScopedAction,
): MemberScope =>
  isRecipientInput(inputId, action?.description)
    ? workspaceMemberScope(stores)
    : solutionMemberScope(stores, actionSolutionId(action, hostSolutionId));
